import type { FormEvent, JSX } from 'react';
import { useState } from 'react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/features/auth/session';
import { useMessages, useSendMessage } from '@/features/messaging/hooks';
import { useHasCapability } from '@/lib/capabilities';

const MAX_BODY_LENGTH = 4000;

/**
 * "Pesan" - the student's single Teacher-Student message thread (Phase 8,
 * Pilar B1; ADR-028). Renders for every capability-holder
 * (`messages:read`): Teacher, Student, and Organization Admin all land
 * here, same as every other capability-driven card in this app - the
 * compose box is additionally gated on `messages:manage`, so Organization
 * Admin sees the thread read-only (deliberate safeguarding oversight, not
 * participation - see ADR-028, Decision 5).
 */
export function MessageThreadCard({ studentId }: { readonly studentId: string }): JSX.Element {
  const { user } = useAuth();
  const [draft, setDraft] = useState('');
  const { data, isLoading, isError } = useMessages(studentId);
  const canSend = useHasCapability('messages:manage');
  const send = useSendMessage(studentId);

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const trimmed = draft.trim();
    if (trimmed.length === 0) {
      return;
    }
    send.mutate(
      { body: trimmed },
      {
        onSuccess: () => {
          setDraft('');
        },
      },
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Pesan</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {isLoading && (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-10 w-3/4" />
            <Skeleton className="h-10 w-3/4 self-end" />
          </div>
        )}

        {isError && (
          <Alert variant="destructive">
            <AlertDescription>Gagal memuat pesan.</AlertDescription>
          </Alert>
        )}

        {!isLoading && !isError && data !== undefined && data.items.length === 0 && (
          <p className="text-sm text-muted-foreground">Belum ada pesan.</p>
        )}

        {!isLoading && !isError && data !== undefined && data.items.length > 0 && (
          <ul className="flex flex-col gap-2">
            {data.items.map((message) => {
              const isMine = message.senderId === user?.userId;
              return (
                <li
                  key={message.id}
                  className={`flex flex-col gap-1 rounded-md border p-3 ${
                    isMine ? 'self-end border-primary/30 bg-primary/5' : 'self-start border-border'
                  } max-w-[85%]`}
                >
                  <span className="text-xs font-medium text-muted-foreground">
                    {message.senderName}
                  </span>
                  <p className="whitespace-pre-wrap break-words text-sm">{message.body}</p>
                  <span className="text-xs text-muted-foreground">
                    {new Date(message.createdAt).toLocaleString('id-ID')}
                  </span>
                </li>
              );
            })}
          </ul>
        )}

        {canSend && (
          <form onSubmit={handleSubmit} className="flex flex-col gap-2">
            <Textarea
              aria-label="Tulis pesan"
              placeholder="Tulis pesan..."
              value={draft}
              maxLength={MAX_BODY_LENGTH}
              onChange={(event) => {
                setDraft(event.target.value);
              }}
            />
            {send.isError && (
              <Alert variant="destructive">
                <AlertDescription>Gagal mengirim pesan.</AlertDescription>
              </Alert>
            )}
            <Button
              type="submit"
              size="sm"
              className="w-fit"
              disabled={send.isPending || draft.trim().length === 0}
            >
              Kirim
            </Button>
          </form>
        )}
      </CardContent>
    </Card>
  );
}
